import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../bizsuite-app';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import RegionPicker from '../components/RegionPicker';
import { useFormDirty } from '../hooks/useFormDirty';
import { formatCurrency } from '../utils/currency';
import { formatDate } from '../utils/date';
import { toast } from '../utils/toast';

/* ── SVG Icons for MultiSelect ── */
const IconChevronDown = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
);
const IconX = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
);

/* ── Multi-select dropdown component ── */
function MultiSelect({ options, selected, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [flipUp, setFlipUp] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleOpen() {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setFlipUp(window.innerHeight - rect.bottom < 220);
    }
    setOpen(!open);
  }

  function toggle(id) {
    const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
    onChange(next);
  }

  const selectedNames = options.filter(o => selected.includes(o.id)).map(o => o.name);

  return (
    <div className="multi-select" ref={ref}>
      <div className="multi-select-trigger" onClick={handleOpen}>
        {selectedNames.length === 0 ? (
          <span className="multi-select-placeholder">{placeholder || 'Select...'}</span>
        ) : (
          <div className="multi-select-tags">
            {selectedNames.map((name, i) => (
              <span key={selected[i]} className="multi-select-tag">
                {name}
                <span className="multi-select-tag-x" onClick={e => { e.stopPropagation(); toggle(selected[i]); }}><IconX /></span>
              </span>
            ))}
          </div>
        )}
        <span className="multi-select-arrow"><IconChevronDown /></span>
      </div>
      {open && (
        <div className={`multi-select-dropdown${flipUp ? ' flip-up' : ''}`}>
          {options.length === 0 && <div className="multi-select-empty">No options</div>}
          {options.map(o => (
            <label key={o.id} className={`multi-select-option${selected.includes(o.id) ? ' checked' : ''}`}>
              <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} />
              {o.name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

const TIER_OPTIONS = ['Strategic', 'Key', 'Standard'];
const STATUS_OPTIONS = ['Active', 'Inactive', 'Prospect', 'Churned'];

const TIER_BADGES = { Strategic: 'badge-purple', Key: 'badge-blue', Standard: 'badge-gray' };
const STATUS_BADGES = { Active: 'badge-green', Inactive: 'badge-gray', Prospect: 'badge-yellow', Churned: 'badge-red' };

const EMPTY_CUSTOMER = {
  company_name: '', industry: '', vertical: '', sub_vertical: '', legal_entity: '',
  company_size: '', management_type: '', tier: 'Standard', status: 'Active',
  website: '', phone: '', address: '', city: '', state: '', country: '', region_id: '', pricing_schema_id: '', payment_term_id: '',
  zip_code: '', notes: '', pan: '', tan: '', gstn: '', gst_state: ''
};

const EMPTY_CONTACT = {
  first_name: '', last_name: '', email: '', phone: '', title: '', is_primary: false
};

function healthLabel(score) {
  if (score >= 80) return { label: 'Excellent', cls: 'excellent' };
  if (score >= 60) return { label: 'Good', cls: 'good' };
  if (score >= 40) return { label: 'Average', cls: 'average' };
  return { label: 'Poor', cls: 'poor' };
}

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_CUSTOMER });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  /* Leads for "Customer from Lead" */
  const [leads, setLeads] = useState([]);
  const [leadPickerOpen, setLeadPickerOpen] = useState(false);

  /* Lookup masters (loaded from metadata) */
  const [allLookups, setAllLookups] = useState([]);
  const [pricingSchemas, setPricingSchemas] = useState([]);

  /* Multi-value selections for divisions & distribution channels */
  const [selectedDivisions, setSelectedDivisions] = useState([]);
  const [selectedDistChannels, setSelectedDistChannels] = useState([]);

  /* Dirty-tracking for unsaved-changes prompts */
  const { dirty, markDirty, resetDirty, confirmIfDirty } = useFormDirty();
  const { dirty: contactDirty, markDirty: markContactDirty, resetDirty: resetContactDirty } = useFormDirty();

  /* Detail / 360 view */
  const [detailCustomer, setDetailCustomer] = useState(null);
  const [detailTab, setDetailTab] = useState('info');
  const [contacts, setContacts] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [creditTerms, setCreditTerms] = useState([]);
  const [contactModal, setContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({ ...EMPTY_CONTACT });
  const [contactErrors, setContactErrors] = useState({});

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await apiFetch('/api/customers');
      if (res.ok) {
        const data = await res.json();
        setCustomers(data.data || data.customers || data || []);
      }
    } catch (err) { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  /* Load lookup masters & leads */
  useEffect(() => {
    async function loadLookups() {
      try {
        const res = await apiFetch('/api/metadata/lookups');
        if (res.ok) { const d = await res.json(); setAllLookups(d.rows || []); }
      } catch (e) { /* ignore */ }
    }
    async function loadPricingSchemas() {
      try {
        const res = await apiFetch('/api/pricing-schemas');
        if (res.ok) { const d = await res.json(); setPricingSchemas(d.schemas || []); }
      } catch (e) { /* ignore */ }
    }
    async function loadLeads() {
      try {
        const res = await apiFetch('/api/crm/leads?limit=500');
        if (res.ok) {
          const d = await res.json();
          const all = d.data || d.leads || d || [];
          setLeads(all.filter(l => (l.status || '').toLowerCase() !== 'disqualified'));
        }
      } catch (e) { /* ignore */ }
    }
    loadLookups();
    loadLeads();
    loadPricingSchemas();
  }, []);

  async function fetchDetail(customer) {
    setDetailCustomer(customer);
    setDetailTab('info');

    const [contactsRes, agreementsRes, creditRes, overviewRes] = await Promise.allSettled([
      apiFetch(`/api/customers/${customer.id}/contacts`),
      apiFetch(`/api/customers/${customer.id}/agreements`),
      apiFetch(`/api/customers/${customer.id}/credit-terms`),
      apiFetch(`/api/customers/${customer.id}/overview`),
    ]);

    if (contactsRes.status === 'fulfilled' && contactsRes.value.ok) {
      const d = await contactsRes.value.json();
      setContacts(d.data || d.contacts || d || []);
    } else { setContacts([]); }

    if (agreementsRes.status === 'fulfilled' && agreementsRes.value.ok) {
      const d = await agreementsRes.value.json();
      setAgreements(d.data || d.agreements || d || []);
    } else { setAgreements([]); }

    if (creditRes.status === 'fulfilled' && creditRes.value.ok) {
      const d = await creditRes.value.json();
      setCreditTerms(d.data || d.credit_terms || d || []);
    } else { setCreditTerms([]); }

    // Load divisions/distribution channels from overview
    if (overviewRes.status === 'fulfilled' && overviewRes.value.ok) {
      const d = await overviewRes.value.json();
      const custData = d.customer || customer;
      custData._divisions = d.divisions || [];
      custData._distChannels = d.distributionChannels || [];
      setDetailCustomer(custData);
    }
  }

  /* Customer CRUD */
  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY_CUSTOMER });
    setErrors({});
    setSelectedDivisions([]);
    setSelectedDistChannels([]);
    resetDirty();
    setModalOpen(true);
  }

  function openFromLead(lead) {
    setEditing(null);
    setLeadPickerOpen(false);
    setForm({
      ...EMPTY_CUSTOMER,
      company_name: lead.company_name || lead.company || '',
      phone: lead.phone || '',
      notes: lead.notes ? `Created from Lead: ${lead.contact_name || ''}. ${lead.notes}` : `Created from Lead: ${lead.contact_name || ''}`
    });
    setErrors({});
    setSelectedDivisions([]);
    setSelectedDistChannels([]);
    resetDirty();
    markDirty();
    setModalOpen(true);
  }

  function openEdit(cust) {
    setEditing(cust);
    setForm({
      company_name: cust.name || '',
      industry: cust.industry || '',
      vertical: cust.vertical || '',
      sub_vertical: cust.sub_vertical || '',
      legal_entity: cust.legal_entity || '',
      company_size: cust.company_size || '',
      management_type: cust.management_type || '',
      tier: cust.tier || 'Standard',
      status: cust.status || 'Active',
      website: cust.website || '',
      phone: cust.phone || '',
      address: cust.address || '',
      city: cust.city || '',
      state: cust.state || '',
      country: cust.country || '',
      region_id: cust.region_id || '',
      pricing_schema_id: cust.pricing_schema_id || '',
      payment_term_id: cust.payment_term_id || '',
      zip_code: cust.zip_code || '',
      notes: cust.notes || '',
      pan: cust.pan || '',
      tan: cust.tan || '',
      gstn: cust.gstn || '',
      gst_state: cust.gst_state || ''
    });
    // M2M: division_ids from list view, _divisions from detail view
    const divIds = cust.division_ids || (cust._divisions || []).map(d => d.lookup_id) || [];
    const dcIds = cust.dist_channel_ids || (cust._distChannels || []).map(d => d.lookup_id) || [];
    setSelectedDivisions(divIds);
    setSelectedDistChannels(dcIds);
    setErrors({});
    resetDirty();
    setModalOpen(true);
  }

  function handleField(name, value) {
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    markDirty();
  }

  function validate() {
    const errs = {};
    if (!form.company_name.trim()) errs.company_name = 'Company name is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSave() {
    if (!validate()) return;
    setSaving(true);
    try {
      const url = editing ? `/api/customers/${editing.id}` : '/api/customers';
      const method = editing ? 'PUT' : 'POST';

      // Backend expects camelCase / different field names
      const body = {
        name: form.company_name,
        industry: form.industry || null,
        vertical: form.vertical || null,
        subVertical: form.sub_vertical || null,
        regionId: form.region_id || null,
        pricingSchemaId: form.pricing_schema_id || null,
        paymentTermId: form.payment_term_id || null,
        legalEntity: form.legal_entity || null,
        companySize: form.company_size || null,
        managementType: form.management_type || null,
        tier: form.tier || null,
        status: form.status || 'Active',
        website: form.website || null,
        phone: form.phone || null,
        address: form.address || null,
        city: form.city || null,
        state: form.state || null,
        country: form.country || null,
        zipCode: form.zip_code || null,
        notes: form.notes || null,
        pan: form.pan || null,
        tan: form.tan || null,
        gstn: form.gstn || null,
        gstState: form.gst_state || null,
      };

      const res = await apiFetch(url, { method, body: JSON.stringify(body) });
      if (res.ok) {
        const resData = await res.json();
        const custId = editing ? editing.id : resData.id;

        // Save divisions and distribution channels (M2M)
        if (custId) {
          await Promise.allSettled([
            apiFetch(`/api/customers/${custId}/divisions`, {
              method: 'POST', body: JSON.stringify({ lookupIds: selectedDivisions })
            }),
            apiFetch(`/api/customers/${custId}/distribution-channels`, {
              method: 'POST', body: JSON.stringify({ lookupIds: selectedDistChannels })
            }),
          ]);
        }

        resetDirty();
        setModalOpen(false);
        fetchCustomers();
        if (detailCustomer && editing && editing.id === detailCustomer.id) {
          fetchDetail(detailCustomer);
        }
      } else {
        const data = await res.json();
        setErrors({ _general: data.error || data.message || 'Failed to save customer' });
      }
    } catch (err) {
      setErrors({ _general: 'Network error. Please try again.' });
    }
    setSaving(false);
  }

  async function handleDelete(cust) {
    if (!(await toast.confirm({ body: `Delete customer "${cust.name}"?`, danger: true }))) return;
    try {
      await apiFetch(`/api/customers/${cust.id}`, { method: 'DELETE' });
      fetchCustomers();
      if (detailCustomer && detailCustomer.id === cust.id) setDetailCustomer(null);
    } catch (err) { /* ignore */ }
  }

  /* Contact CRUD */
  function openContactCreate() {
    setEditingContact(null);
    setContactForm({ ...EMPTY_CONTACT });
    setContactErrors({});
    resetContactDirty();
    setContactModal(true);
  }

  function openContactEdit(contact) {
    setEditingContact(contact);
    // DB stores single 'name' field and 'designation' (not title)
    const nameParts = (contact.name || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    setContactForm({
      first_name: firstName,
      last_name: lastName,
      email: contact.email || '',
      phone: contact.phone || '',
      title: contact.designation || '',
      is_primary: contact.is_primary || false
    });
    setContactErrors({});
    resetContactDirty();
    setContactModal(true);
  }

  function handleContactField(name, value) {
    setContactForm(prev => ({ ...prev, [name]: value }));
    markContactDirty();
  }

  async function handleContactSave() {
    const errs = {};
    if (!contactForm.first_name.trim()) errs.first_name = 'First name is required';
    if (!contactForm.last_name.trim()) errs.last_name = 'Last name is required';
    setContactErrors(errs);
    if (Object.keys(errs).length > 0) return;

    // Backend expects: name (combined), designation (not title), isPrimary (not is_primary)
    const body = {
      name: `${contactForm.first_name.trim()} ${contactForm.last_name.trim()}`.trim(),
      email: contactForm.email || null,
      phone: contactForm.phone || null,
      designation: contactForm.title || null,
      isPrimary: contactForm.is_primary || false,
    };

    try {
      const url = editingContact
        ? `/api/customers/${detailCustomer.id}/contacts/${editingContact.id}`
        : `/api/customers/${detailCustomer.id}/contacts`;
      const method = editingContact ? 'PUT' : 'POST';
      const res = await apiFetch(url, { method, body: JSON.stringify(body) });
      if (res.ok) {
        resetContactDirty();
        setContactModal(false);
        fetchDetail(detailCustomer);
      }
    } catch (err) { /* ignore */ }
  }

  /* Table columns */
  const columns = [
    { key: 'name', label: 'Company', sortable: true },
    { key: 'industry', label: 'Industry', sortable: true },
    { key: 'division_names', label: 'Divisions', render: (val) => val || '—' },
    {
      key: 'tier', label: 'Tier', sortable: true,
      render: (val) => <span className={`badge ${TIER_BADGES[val] || 'badge-gray'}`}>{val || 'Standard'}</span>
    },
    {
      key: 'status', label: 'Status', sortable: true,
      render: (val) => <span className={`badge ${STATUS_BADGES[val] || 'badge-gray'}`}>{val || 'Active'}</span>
    },
    {
      key: 'health_score', label: 'Health', sortable: true, type: 'number',
      render: (val) => {
        if (val == null) return '—';
        const h = healthLabel(val);
        return (
          <span className="health-score">
            <span className={`health-dot ${h.cls}`}></span>
            {h.label} ({val})
          </span>
        );
      }
    },
    {
      key: 'created_at', label: 'Created', sortable: true, type: 'date',
      render: (val) => formatDate(val)
    }
  ];

  const tableActions = [
    { label: 'View', icon: '\u{1F441}', onClick: (row) => fetchDetail(row) },
    { label: 'Edit', icon: 'edit', onClick: openEdit },
    { label: 'Delete', icon: 'delete', onClick: handleDelete }
  ];

  /* Detail 360 view */
  if (detailCustomer) {
    const c = detailCustomer;
    const initials = (c.name || '??').substring(0, 2).toUpperCase();
    const h = healthLabel(c.health_score || 50);

    return (
      <div>
        <button className="btn btn-secondary btn-sm" style={{ marginBottom: 16, marginTop: -40 }} onClick={() => { if (confirmIfDirty()) setDetailCustomer(null); }}>
          {'\u2190'} Back to Overview
        </button>

        <div className="detail-header">
          <div className="detail-avatar">{initials}</div>
          <div className="detail-info">
            <h2>{c.name}</h2>
            <div className="meta">
              <span className={`badge ${TIER_BADGES[c.tier] || 'badge-gray'}`}>{c.tier || 'Standard'}</span>
              <span className={`badge ${STATUS_BADGES[c.status] || 'badge-gray'}`}>{c.status || 'Active'}</span>
              <span className="health-score">
                <span className={`health-dot ${h.cls}`}></span>
                {h.label}
              </span>
              {c.industry && <span>{c.industry}</span>}
            </div>
          </div>
          <div className="detail-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => openEdit(c)}>Edit</button>
          </div>
        </div>

        <div className="tabs">
          {['info', 'contacts', 'agreements', 'credit'].map(tab => (
            <div
              key={tab}
              className={`tab${detailTab === tab ? ' active' : ''}`}
              onClick={() => setDetailTab(tab)}
            >
              {tab === 'info' ? 'Information' : tab === 'contacts' ? 'Contacts' : tab === 'agreements' ? 'Agreements' : 'Credit Terms'}
            </div>
          ))}
        </div>

        {detailTab === 'info' && (
          <div className="card">
            <div className="card-body">
              <div className="form-row">
                <div className="form-group">
                  <div className="form-label">Vertical</div>
                  <div>{c.vertical || '—'}</div>
                </div>
                <div className="form-group">
                  <div className="form-label">Industry</div>
                  <div>{c.industry || '—'}</div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <div className="form-label">Sub-Vertical</div>
                  <div>{c.sub_vertical || '—'}</div>
                </div>
                <div className="form-group">
                  <div className="form-label">Legal Entity</div>
                  <div>{c.legal_entity || '—'}</div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <div className="form-label">Size</div>
                  <div>{c.company_size || '—'}</div>
                </div>
                <div className="form-group">
                  <div className="form-label">Management Type</div>
                  <div>{c.management_type || '—'}</div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <div className="form-label">Divisions</div>
                  <div>{(c._divisions || []).map(d => d.name).join(', ') || c.division_names || '—'}</div>
                </div>
                <div className="form-group">
                  <div className="form-label">Distribution Channels</div>
                  <div>{(c._distChannels || []).map(d => d.name).join(', ') || c.dist_channel_names || '—'}</div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <div className="form-label">Website</div>
                  <div>{c.website || '—'}</div>
                </div>
                <div className="form-group">
                  <div className="form-label">Phone</div>
                  <div>{c.phone || '—'}</div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <div className="form-label">Address</div>
                  <div>{[c.address, c.city, c.state, c.zip_code, c.country].filter(Boolean).join(', ') || '—'}</div>
                </div>
                <div className="form-group">
                  <div className="form-label">Notes</div>
                  <div>{c.notes || '—'}</div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <div className="form-label">PAN</div>
                  <div>{c.pan || '—'}</div>
                </div>
                <div className="form-group">
                  <div className="form-label">TAN</div>
                  <div>{c.tan || '—'}</div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <div className="form-label">GSTN</div>
                  <div>{c.gstn || '—'}</div>
                </div>
                <div className="form-group">
                  <div className="form-label">GST State</div>
                  <div>{c.gst_state || '—'}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {detailTab === 'contacts' && (
          <div className="card">
            <div className="card-header">
              <h2>Contacts</h2>
              <button className="btn btn-primary btn-sm" onClick={openContactCreate}>+ Add Contact</button>
            </div>
            <div className="card-body">
              {contacts.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#94A3B8' }}>No contacts found</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th><th>Title</th><th>Email</th><th>Phone</th><th>Primary</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map(ct => (
                      <tr key={ct.id}>
                        <td>{ct.name || '—'}</td>
                        <td>{ct.designation || '—'}</td>
                        <td>{ct.email || '—'}</td>
                        <td>{ct.phone || '—'}</td>
                        <td>{ct.is_primary ? <span className="badge badge-green">Primary</span> : '—'}</td>
                        <td className="actions-cell">
                          <button onClick={() => openContactEdit(ct)} title="Edit">{'\u270F'}</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {detailTab === 'agreements' && (
          <div className="card">
            <div className="card-header"><h2>Agreements</h2></div>
            <div className="card-body">
              {agreements.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#94A3B8' }}>No agreements found</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr><th>Type</th><th>Start</th><th>End</th><th>Value</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {agreements.map(ag => (
                      <tr key={ag.id}>
                        <td>{ag.type || ag.agreement_type || '—'}</td>
                        <td>{formatDate(ag.start_date)}</td>
                        <td>{formatDate(ag.end_date)}</td>
                        <td>{ag.value ? formatCurrency(ag.value) : '—'}</td>
                        <td><span className={`badge ${ag.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>{ag.status || '—'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {detailTab === 'credit' && (
          <div className="card">
            <div className="card-header"><h2>Credit Terms</h2></div>
            <div className="card-body">
              {creditTerms.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#94A3B8' }}>No credit terms found</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr><th>Payment Terms</th><th>Credit Limit</th><th>Currency</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {creditTerms.map(ct => (
                      <tr key={ct.id}>
                        <td>{ct.payment_terms || '—'}</td>
                        <td>{ct.credit_limit ? formatCurrency(ct.credit_limit) : '—'}</td>
                        <td>{ct.currency || 'INR'}</td>
                        <td><span className={`badge ${ct.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>{ct.status || '—'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Contact Modal */}
        <Modal
          title={editingContact ? 'Edit Contact' : 'Add Contact'}
          isOpen={contactModal}
          onClose={() => setContactModal(false)}
          size="md"
          dirty={contactDirty}
        >
          <div className="form-row">
            <FormField label="First Name" name="first_name" value={contactForm.first_name} onChange={handleContactField} error={contactErrors.first_name} required />
            <FormField label="Last Name" name="last_name" value={contactForm.last_name} onChange={handleContactField} error={contactErrors.last_name} required />
          </div>
          <div className="form-row">
            <FormField label="Email" name="email" type="email" value={contactForm.email} onChange={handleContactField} />
            <FormField label="Phone" name="phone" value={contactForm.phone} onChange={handleContactField} />
          </div>
          <FormField label="Job Title" name="title" value={contactForm.title} onChange={handleContactField} />
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={contactForm.is_primary} onChange={(e) => handleContactField('is_primary', e.target.checked)} />
              Primary Contact
            </label>
          </div>
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setContactModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleContactSave}>
              {editingContact ? 'Update' : 'Add'} Contact
            </button>
          </div>
        </Modal>

        {/* Customer Edit Modal (reused) */}
        <Modal title="Edit Customer" isOpen={modalOpen} onClose={() => setModalOpen(false)} size="lg" dirty={dirty}>
          {renderCustomerForm()}
        </Modal>
      </div>
    );
  }

  function renderCustomerForm() {
    // Build cascading lookup options
    const verticals = allLookups.filter(l => l.category === 'vertical' && l.status === 'Active');
    const selectedVerticalId = verticals.find(v => v.name === form.vertical)?.id;
    const industries = allLookups.filter(l => l.category === 'industry' && l.status === 'Active' && (!selectedVerticalId || l.parent_id === selectedVerticalId));
    const selectedIndustryId = industries.find(i => i.name === form.industry)?.id;
    const subVerticals = allLookups.filter(l => l.category === 'sub_vertical' && l.status === 'Active' && (!selectedIndustryId || l.parent_id === selectedIndustryId));
    const legalEntities = allLookups.filter(l => l.category === 'legal_entity' && l.status === 'Active');
    const sizes = allLookups.filter(l => l.category === 'size' && l.status === 'Active');
    const mgmtTypes = allLookups.filter(l => l.category === 'management_type' && l.status === 'Active');

    function handleVerticalChange(name, value) {
      handleField(name, value);
      // Clear dependent cascading fields
      setForm(prev => ({ ...prev, vertical: value, industry: '', sub_vertical: '' }));
    }
    function handleIndustryChange(name, value) {
      handleField(name, value);
      setForm(prev => ({ ...prev, industry: value, sub_vertical: '' }));
    }

    return (
      <>
        {errors._general && (
          <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
            {errors._general}
          </div>
        )}
        <div className="form-row">
          <FormField label="Company Name" name="company_name" value={form.company_name} onChange={handleField} error={errors.company_name} required />
          <FormField label="Legal Entity" name="legal_entity" type="select" value={form.legal_entity} onChange={handleField} options={legalEntities.map(l => l.name)} />
        </div>
        <div className="form-row">
          <FormField label="Vertical" name="vertical" type="select" value={form.vertical} onChange={handleVerticalChange} options={verticals.map(v => v.name)} />
          <FormField label="Industry" name="industry" type="select" value={form.industry} onChange={handleIndustryChange} options={industries.map(i => i.name)} />
        </div>
        <div className="form-row">
          <FormField label="Sub-Vertical" name="sub_vertical" type="select" value={form.sub_vertical} onChange={handleField} options={subVerticals.map(s => s.name)} />
          <FormField label="Size" name="company_size" type="select" value={form.company_size} onChange={handleField} options={sizes.map(s => s.name)} />
        </div>
        <div className="form-row">
          <FormField label="Management Type" name="management_type" type="select" value={form.management_type} onChange={handleField} options={mgmtTypes.map(m => m.name)} />
          <FormField label="Tier" name="tier" type="select" value={form.tier} onChange={handleField} options={TIER_OPTIONS} />
        </div>
        <div className="form-row">
          <FormField label="Status" name="status" type="select" value={form.status} onChange={handleField} options={STATUS_OPTIONS} />
          <div></div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Divisions</label>
            <MultiSelect
              options={allLookups.filter(l => l.category === 'division' && l.status === 'Active')}
              selected={selectedDivisions}
              onChange={v => { setSelectedDivisions(v); markDirty(); }}
              placeholder="Select divisions..."
            />
          </div>
          <div className="form-group">
            <label className="form-label">Distribution Channels</label>
            <MultiSelect
              options={allLookups.filter(l => l.category === 'distribution_channel' && l.status === 'Active')}
              selected={selectedDistChannels}
              onChange={v => { setSelectedDistChannels(v); markDirty(); }}
              placeholder="Select channels..."
            />
          </div>
        </div>
        <div className="form-row">
          <FormField label="Website" name="website" value={form.website} onChange={handleField} placeholder="https://" />
          <FormField label="Phone" name="phone" value={form.phone} onChange={handleField} />
        </div>
        <FormField label="Address" name="address" value={form.address} onChange={handleField} />
        <div className="form-row">
          <FormField label="City" name="city" value={form.city} onChange={handleField} />
          <FormField label="State" name="state" value={form.state} onChange={handleField} />
        </div>
        <div className="form-row">
          <FormField label="ZIP Code" name="zip_code" value={form.zip_code} onChange={handleField} />
          {/* Country + Region — cascading dropdowns from metadata_lookups
              masters. Country narrows the region list. */}
          <RegionPicker label="Country & Region" value={form.region_id}
            onChange={(v) => handleField('region_id', v)} placeholder="Select region..." />
        </div>
        <div className="form-row">
          <FormField label="PAN" name="pan" value={form.pan} onChange={handleField} placeholder="e.g. ABCDE1234F" />
          <FormField label="TAN" name="tan" value={form.tan} onChange={handleField} placeholder="e.g. ABCD12345E" />
        </div>
        <div className="form-row">
          <FormField label="GSTN" name="gstn" value={form.gstn} onChange={handleField} placeholder="e.g. 27ABCDE1234F1Z5" />
          <FormField label="GST State" name="gst_state" value={form.gst_state} onChange={handleField} placeholder="e.g. Maharashtra" />
        </div>
        {/* Default Pricing Schema for this customer. Contracts and estimates
            created for this customer will pre-fill from here, but the user
            can still override on each contract / estimate header. */}
        <FormField label="Default Pricing Schema" name="pricing_schema_id" type="select"
          value={form.pricing_schema_id} onChange={handleField}
          options={[
            { value: '', label: '— None —' },
            ...pricingSchemas.filter(s => s.status === 'Active').map(s => ({ value: s.id, label: `${s.code} · ${s.name}` })),
          ]}
          placeholder="Select pricing schema..." />
        {/* Default Payment Terms — pulled from System → Metadata → Lookups (payment_terms category).
            This auto-fills into contracts created for the customer; user can still override per-contract. */}
        <FormField label="Default Payment Terms" name="payment_term_id" type="select"
          value={form.payment_term_id} onChange={handleField}
          options={[
            { value: '', label: '— None —' },
            ...allLookups
              .filter(l => l.category === 'payment_terms' && l.status !== 'Inactive')
              .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
              .map(l => ({ value: l.id, label: l.days != null ? `${l.name} (${l.days}d)` : l.name })),
          ]}
          placeholder="Select payment terms..." />
        <FormField label="Notes" name="notes" type="textarea" value={form.notes} onChange={handleField} />
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : (editing ? 'Update Customer' : 'Create Customer')}
          </button>
        </div>
      </>
    );
  }

  /* List view */
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 16, marginTop: -40 }}>
        <button className="btn btn-secondary" onClick={() => setLeadPickerOpen(true)}>+ Customer from Lead</button>
        <button className="btn btn-primary" onClick={openCreate}>+ New Customer</button>
      </div>

      <DataTable
        columns={columns}
        data={customers}
        actions={tableActions}
        loading={loading}
        emptyMessage="No customers found. Add your first customer to get started."
        emptyIcon={'\u{1F465}'}
        onRowClick={(row) => fetchDetail(row)}
      />

      <Modal
        title={editing ? 'Edit Customer' : 'New Customer'}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        size="lg"
        dirty={dirty}
      >
        {renderCustomerForm()}
      </Modal>

      {/* Lead Picker Modal */}
      <Modal
        title="Select Lead to Create Customer"
        isOpen={leadPickerOpen}
        onClose={() => setLeadPickerOpen(false)}
        size="md"
      >
        {leads.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#94A3B8' }}>No eligible leads found (non-disqualified)</div>
        ) : (
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr><th>Company</th><th>Contact</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {leads.map(lead => (
                  <tr key={lead.id}>
                    <td>{lead.company_name || lead.company || '—'}</td>
                    <td>{lead.contact_name || '—'}</td>
                    <td><span className="badge badge-blue">{lead.status || 'new'}</span></td>
                    <td>
                      <button className="btn btn-primary btn-sm" onClick={() => openFromLead(lead)}>
                        Select
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="form-actions" style={{ marginTop: 12 }}>
          <button className="btn btn-secondary" onClick={() => setLeadPickerOpen(false)}>Cancel</button>
        </div>
      </Modal>
    </div>
  );
}
